import type {SxProps} from '@mui/material';
import TextField from '@mui/material/TextField';
import {lighten} from '@mui/system/colorManipulator';
import type {ChangeEvent, FC, FocusEvent, KeyboardEvent, ReactNode} from 'react';
import {useEffect, useState} from 'react';
import {secondaryColor} from '@/theme/theme';
import {hasDecimalSeparator, isDecimalSeparatorKey, parseDecimalInput, sanitizeDecimalInput} from './decimalInput';

//TYPES
type Props = {
  label?: string;
  size?: 'small' | 'medium';
  value?: number;
  placeholder?: string;
  required?: boolean;
  startAdornment?: ReactNode;
  onChange: (value: number) => void;
  direction?: 'column' | 'row';
  disabled?: boolean;
  min?: number;
  max?: number;
};

const InputNumber: FC<Props> = ({
  value,
  size = 'small',
  onChange,
  placeholder,
  required = false,
  startAdornment,
  disabled = false,
  min,
  max,
}) => {
  const [rawValue, setRawValue] = useState<string>(value === undefined ? '' : String(value));

  useEffect(() => {
    setRawValue(value === undefined ? '' : String(value));
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setRawValue(sanitizeDecimalInput(e.target.value));
  };

  const commitValue = () => {
    const parsed = parseDecimalInput(rawValue);
    if (parsed === undefined) {
      setRawValue(value === undefined ? '' : String(value));
      return;
    }
    if (min !== undefined && parsed < min) {
      setRawValue(value === undefined ? '' : String(value));
      return;
    }
    if (max !== undefined && parsed > max) {
      setRawValue(value === undefined ? '' : String(value));
      return;
    }
    if (parsed !== value) {
      onChange(parsed);
    } else {
      setRawValue(String(value));
    }
  };

  const handleBlur = (_e: FocusEvent<HTMLInputElement>) => {
    commitValue();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ];

    const isNumber = /^[0-9]$/.test(e.key);

    if (e.key === 'Enter') {
      commitValue();
      return;
    }

    if (
      !isNumber &&
      !allowedKeys.includes(e.key) &&
      !(isDecimalSeparatorKey(e.key) && !hasDecimalSeparator(rawValue))
    ) {
      e.preventDefault();
    }
  };

  return (
    <TextField
      size={size}
      variant="outlined"
      value={rawValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      placeholder={placeholder}
      required={required}
      fullWidth
      type="text"
      inputMode="decimal"
      slotProps={{
        input: {startAdornment},
        htmlInput: {min, max},
      }}
      sx={styles.textField}
      disabled={disabled}
    />
  );
};
export default InputNumber;

const styles: Record<string, SxProps> = {
  textField: {
    width: '100%',
    mb: 1,
    bgcolor: lighten(secondaryColor, 0.95),
    '& .MuiOutlinedInput-root': {
      borderRadius: '0px',
      flex: 1,
      '&:hover fieldset': {
        borderRadius: '0px',
        borderColor: 'primary.main', // borde hover
      },
      '&.Mui-focused fieldset': {
        borderColor: 'secondary.main', // borde focus
      },
    },
  },
};
